import { useEffect } from 'react'

type ReviewsWidgetCleanup = {
  disconnect: () => void
  restore: () => void
}

const setupReviewsWidget = (reviewsWidgetValue: string): ReviewsWidgetCleanup => {
  let originalReviewsWidgetBottom: { value: string; priority: string } | null = null

  const getReviewsWidget = () => document.querySelector<HTMLElement>('.R-LauncherButton') ?? null

  const rememberOriginalBottom = (element: HTMLElement) => {
    if (originalReviewsWidgetBottom == null) {
      originalReviewsWidgetBottom = {
        value: element.style.getPropertyValue('bottom'),
        priority: element.style.getPropertyPriority('bottom'),
      }
    }
  }

  const applyReviewsWidgetOffset = (element: HTMLElement) => {
    rememberOriginalBottom(element)
    element.style.setProperty('bottom', reviewsWidgetValue, 'important')
  }

  const restoreReviewsWidgetOffset = (element: HTMLElement) => {
    if (originalReviewsWidgetBottom?.value) {
      element.style.setProperty(
        'bottom',
        originalReviewsWidgetBottom.value,
        originalReviewsWidgetBottom.priority,
      )
    } else {
      element.style.removeProperty('bottom')
    }
  }

  let reviewsWidgetElement = getReviewsWidget()
  if (reviewsWidgetElement) applyReviewsWidgetOffset(reviewsWidgetElement)

  const reviewsWidgetObserver = new MutationObserver(() => {
    if (reviewsWidgetElement) {
      applyReviewsWidgetOffset(reviewsWidgetElement)
    }
  })

  const observeReviewsWidget = (element: HTMLElement) => {
    reviewsWidgetObserver.observe(element, {
      attributes: true,
      attributeFilter: ['style'],
    })
  }

  let bodyObserver: MutationObserver | null = null

  if (reviewsWidgetElement) {
    observeReviewsWidget(reviewsWidgetElement)
  } else {
    // If the widget is not found, observe the body for changes to find it when it's added to the DOM
    bodyObserver = new MutationObserver(() => {
      const foundWidget = getReviewsWidget()
      if (!foundWidget) return

      reviewsWidgetElement = foundWidget
      applyReviewsWidgetOffset(reviewsWidgetElement)

      bodyObserver?.disconnect()
      observeReviewsWidget(reviewsWidgetElement)
    })

    bodyObserver.observe(document.body, { childList: true, subtree: true })
  }

  return {
    disconnect: () => {
      bodyObserver?.disconnect()
      reviewsWidgetObserver.disconnect()
    },
    restore: () => {
      const reviewsWidget = reviewsWidgetElement ?? getReviewsWidget()
      if (reviewsWidget) restoreReviewsWidgetOffset(reviewsWidget)
    },
  }
}

export const useAccountBottomNavOffset = (navEnabled: boolean, offset: string) => {
  useEffect(() => {
    if (!navEnabled) return

    document.documentElement.style.setProperty('--account-bottom-nav-offset', offset)

    return () => {
      document.documentElement.style.removeProperty('--account-bottom-nav-offset')
    }
  }, [navEnabled, offset])
}

export const useReviewsWidgetOffset = (navEnabled: boolean, offset: string) => {
  useEffect(() => {
    if (!navEnabled) return

    const { disconnect, restore } = setupReviewsWidget(offset)

    return () => {
      disconnect()
      restore()
    }
  }, [navEnabled, offset])
}
